import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  PurchaseOrder,
  PurchaseOrderCreateBody,
  PurchaseOrderUpdateBody,
  PurchaseOrderingConfigBody,
} from '@/api/endpoints/purchase-orders'
import {
  purchaseOrderingConfigApi,
  purchaseOrdersApi,
} from '@/api/endpoints/purchase-orders'

/** The list is a DataTable whose cache key is one string; everything is matched by this prefix. */
export const PURCHASE_ORDERS_KEY = 'purchase-orders'

export const purchaseOrderKey = (restaurantId: number, id: string) => [
  'purchase-order',
  restaurantId,
  id,
]

export const orderingStatusKey = (restaurantId: number) => [
  'purchase-ordering-status',
  restaurantId,
]

export const orderingConfigKey = (restaurantId: number) => [
  'purchase-ordering-config',
  restaurantId,
]

/** Whether the Standort can send orders — readable with `purchase_order:view`. */
export function useOrderingStatus(restaurantId: number | null, enabled = true) {
  return useQuery({
    queryKey: orderingStatusKey(restaurantId ?? 0),
    queryFn: () => purchaseOrdersApi.ordering(restaurantId as number),
    enabled: enabled && restaurantId != null,
    retry: false,
    staleTime: 60_000,
  })
}

export function usePurchaseOrder(
  restaurantId: number | null,
  id: string | null,
) {
  return useQuery({
    queryKey: purchaseOrderKey(restaurantId ?? 0, id ?? ''),
    queryFn: () => purchaseOrdersApi.get(restaurantId as number, id as string),
    enabled: restaurantId != null && id != null,
    retry: false,
  })
}

/**
 * Every write changes the list; a send also creates an announced delivery, so the delivery list,
 * the inventory's Eingehend and the Bestellvorschlag's Zukauf go stale too.
 */
function useInvalidate(restaurantId: number | null) {
  const queryClient = useQueryClient()
  return (order?: PurchaseOrder, sent = false) => {
    queryClient.invalidateQueries({
      predicate: (q) => {
        const head = q.queryKey[0]
        if (typeof head !== 'string') return false
        if (head.startsWith(PURCHASE_ORDERS_KEY)) return true
        return (
          sent &&
          (head.startsWith('inventory-deliveries') ||
            head.startsWith('inventory-page') ||
            head === 'inventory' ||
            head === 'order-proposal')
        )
      },
    })
    if (order) {
      queryClient.setQueryData(
        purchaseOrderKey(restaurantId ?? 0, order.id),
        order,
      )
    }
  }
}

export function useCreatePurchaseOrder(restaurantId: number | null) {
  const invalidate = useInvalidate(restaurantId)
  return useMutation({
    mutationFn: (body: PurchaseOrderCreateBody) =>
      purchaseOrdersApi.create(restaurantId as number, body),
    onSuccess: (order) => invalidate(order),
  })
}

export function useUpdatePurchaseOrder(
  restaurantId: number | null,
  id: string | null,
) {
  const invalidate = useInvalidate(restaurantId)
  return useMutation({
    mutationFn: (body: PurchaseOrderUpdateBody) =>
      purchaseOrdersApi.update(restaurantId as number, id as string, body),
    onSuccess: (order) => invalidate(order),
  })
}

export function useDeletePurchaseOrder(
  restaurantId: number | null,
  id: string | null,
) {
  const invalidate = useInvalidate(restaurantId)
  return useMutation({
    mutationFn: () =>
      purchaseOrdersApi.remove(restaurantId as number, id as string),
    onSuccess: () => invalidate(),
  })
}

export function useSendPurchaseOrder(
  restaurantId: number | null,
  id: string | null,
) {
  const queryClient = useQueryClient()
  const invalidate = useInvalidate(restaurantId)
  return useMutation({
    mutationFn: (confirmAfterCutoff: boolean) =>
      purchaseOrdersApi.send(restaurantId as number, id as string, {
        confirmAfterCutoff,
      }),
    onSuccess: (order) => invalidate(order, true),
    // A failed mail leaves the order FAILED with its reason: reload it either way.
    onError: () => {
      invalidate()
      queryClient.invalidateQueries({
        queryKey: purchaseOrderKey(restaurantId ?? 0, id ?? ''),
      })
    },
  })
}

export function usePurchaseOrderingConfig(restaurantId: number) {
  return useQuery({
    queryKey: orderingConfigKey(restaurantId),
    queryFn: () => purchaseOrderingConfigApi.get(restaurantId),
    staleTime: 0,
  })
}

export function useSavePurchaseOrderingConfig(restaurantId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PurchaseOrderingConfigBody) =>
      purchaseOrderingConfigApi.save(restaurantId, body),
    onSuccess: (config) => {
      queryClient.setQueryData(orderingConfigKey(restaurantId), config)
      queryClient.invalidateQueries({
        queryKey: orderingStatusKey(restaurantId),
      })
    },
  })
}
